import React, { useEffect, useState } from "react";
import { Grid, TextField } from "@material-ui/core";
import { Select } from "@ofqual/portal-components/components/Select";
import { ExperimentalFeature } from "@ofqual/portal-components/components/ExperimentalFeature";
import { Controller } from "react-hook-form";
import { ContactTheRegulator } from "../../domain/ContactTheRegulator";
import { SectionProps } from "./SectionProps";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import {
  categoriesService,
  generalConditionsService,
  tagsService,
} from "../../services";
import { ItemType } from "@ofqual/portal-components/components/types";
import {
  injectHeaders,
  toGroupedList,
} from "@ofqual/portal-core/core/utils/listHelpers";
import { Autocomplete } from "@material-ui/lab";
import { useAuth } from "@ofqual/portal-core/core/hooks/useAuth";
import { authSelector } from "../../redux/selectors";

export const OfqualDetails: React.FC<SectionProps<ContactTheRegulator>> = ({
  control,
  errors,
  disabled,
  lookupTracker,
}) => {
  const [tagsLookups, categoriesLookups, generalConditionsLookups] = useLookup(
    {
      service: tagsService,
      id: "id",
      label: "tag",
    },
    categoriesService,
    generalConditionsService
  );

  const groupedTagsLookup = toGroupedList(tagsLookups, {
    id: "id",
    label: "tag",
    group: "tagCategory",
  });

  const { experimentalFeatures } = useAuth(authSelector);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="ctrCategorys"
          as={
            <Select
              label="Categories"
              readOnly={disabled}
              selectVariant="multiple"
              datatest="ctr-categories"
              lookupTracker={lookupTracker}
            >
              {categoriesLookups}
            </Select>
          }
        />
      </Grid>
      <ExperimentalFeature
        experimentalModuleName="portal-comms-cao"
        experimentalFeatures={experimentalFeatures}
        experimentalFeatureVisibleName="ctrConditionsAutocomplete"
      >
        <Grid item xs={12}>
          <Controller
            control={control}
            name="ctrConditions"
            render={({ onChange, value, ...props }) => (
              <Autocomplete
                id="ctrConditionsAutocomplete"
                data-testid="ctr-conditionsAutocomplete"
                groupBy={(option) => option.group}
                options={generalConditionsLookups}
                getOptionLabel={(option) => option.label}
                getOptionSelected={(option, value) => {
                  return value.label === option.label;
                }}
                value={
                  generalConditionsLookups.isPlaceholder === true
                    ? generalConditionsLookups
                    : value.map((y: number) =>
                        generalConditionsLookups.find((x) => x.id === y)
                      )
                }
                style={{ width: "auto" }}
                ListboxProps={{
                  style: { maxHeight: "30rem" },
                  position: "auto-start",
                }}
                multiple
                ChipProps={{ color: "primary" }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Conditions"
                    variant="outlined"
                  />
                )}
                onChange={(_: unknown, data: any[]) => {
                  return onChange(data.map((x) => x.id));
                }}
                {...props}
              />
            )}
          />
        </Grid>
      </ExperimentalFeature>

      <ExperimentalFeature
        experimentalModuleName="portal-comms-cao"
        experimentalFeatures={experimentalFeatures}
        experimentalFeatureHiddenName="ctrConditionsAutocomplete"
      >
        <Grid item xs={12}>
          <Controller
            control={control}
            name="ctrConditions"
            as={
              <Select
                label="Conditions"
                readOnly={disabled}
                selectVariant="multiple"
                datatest="ctr-conditions"
                lookupTracker={lookupTracker}
              >
                {injectHeaders(generalConditionsLookups, (item) => item.group)}
              </Select>
            }
          />
        </Grid>
      </ExperimentalFeature>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="resourceTags"
          as={
            <Select
              selectVariant="checkboxes"
              label="Tags"
              datatest="tags"
              readOnly={disabled}
              lookupTracker={lookupTracker}
            >
              {groupedTagsLookup as ItemType[]}
            </Select>
          }
        />
      </Grid>
    </Grid>
  );
};
